// Hook que carga los ejercicios prescritos al paciente activo,
// derivados de plan_ejercicios → planes (estado='activo') → tratamientos
// (estado='activo') → pacientes.
//
// Modelo: el schema NO tiene "sesiones programadas". Una sesión nace
// como fila en `sesiones` cuando el paciente clickea "Empezar"
// (ver useCrearSesion). Acá listamos lo que el paciente *tiene asignado*,
// no lo que ya hizo.
//
// Estado derivado de sesiones de HOY (today UTC):
//   - 'completado' si tiene una sesion con estado='completada' y
//     started_at >= today (zona horaria del cliente).
//   - 'actual'      = primer NO-completado del orden.
//   - 'pendiente'   = el resto.
//
// Esto reemplaza el mock previo (`idx === 0 ? "actual" : "pendiente"`)
// que ignoraba el historial — refetch del hook después de completar una
// sesión avanza el "actual" al siguiente sin recargar la página.
//
// paciente_id resuelto vía useCurrentPacienteId() (string | null).
// Early return en useEffect si null (loading / lookup-pending / huérfano).

import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/lib/supabase/client";
import { useCurrentPacienteId } from "@/app/components/auth/AuthProvider";
import type { EjercicioHoy } from "@/app/pages/HomePaciente";

interface State {
  data: EjercicioHoy[] | null;
  error: string | null;
  loading: boolean;
}

export interface UseEjerciciosDelDiaReturn extends State {
  /** Re-ejecuta ambas queries (plan_ejercicios + sesiones de hoy).
   *  Útil después de skip (QA) o de completar una sesión real para
   *  avanzar el "actual" al siguiente ejercicio sin recargar página. */
  refetch: () => Promise<void>;
}

/** Shape de la query de plan_ejercicios con join a ejercicios. */
interface PlanEjercicioRow {
  id: string;
  ejercicio_id: string;
  reps_objetivo: number;
  series_objetivo: number;
  orden: number;
  ejercicios: {
    nombre: string;
  } | null;
}

/** Devuelve el inicio del día (00:00:00 local) en ISO. Usado como bound
 *  inferior del filtro `started_at >= hoy` para detectar sesiones de hoy. */
function startOfTodayISO(): string {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return start.toISOString();
}

export function useEjerciciosDelDia(): UseEjerciciosDelDiaReturn {
  const pacienteId = useCurrentPacienteId();
  const [state, setState] = useState<State>({
    data: null,
    error: null,
    loading: true,
  });

  const fetchAll = useCallback(async (signal?: { cancelled: boolean }) => {
    if (!pacienteId) {
      setState({ data: null, error: null, loading: false });
      return;
    }
    setState((s) => ({ ...s, loading: true }));

    // Query 1: plan_ejercicios activos del paciente, ordenados.
    const planEjerciciosQ = supabase
      .from("plan_ejercicios")
      .select(
        `
        id,
        ejercicio_id,
        reps_objetivo,
        series_objetivo,
        orden,
        ejercicios!inner(nombre),
        planes!inner(
          estado,
          tratamientos!inner(
            paciente_id,
            estado
          )
        )
      `,
      )
      .eq("planes.estado", "activo")
      .eq("planes.tratamientos.paciente_id", pacienteId)
      .eq("planes.tratamientos.estado", "activo")
      .order("orden", { ascending: true })
      .returns<PlanEjercicioRow[]>();

    // Query 2: plan_ejercicio_ids de sesiones COMPLETADAS hoy.
    const sesionesHoyQ = supabase
      .from("sesiones")
      .select("plan_ejercicio_id")
      .eq("paciente_id", pacienteId)
      .eq("estado", "completada")
      .gte("started_at", startOfTodayISO())
      .returns<{ plan_ejercicio_id: string }[]>();

    const [planEjerciciosRes, sesionesHoyRes] = await Promise.all([
      planEjerciciosQ,
      sesionesHoyQ,
    ]);

    if (signal?.cancelled) return;

    if (planEjerciciosRes.error) {
      console.warn(
        "[useEjerciciosDelDia] error cargando plan_ejercicios",
        planEjerciciosRes.error,
      );
      setState({
        data: null,
        error: planEjerciciosRes.error.message,
        loading: false,
      });
      return;
    }

    // sesiones de hoy es secundario — si falla, asumimos lista vacía y
    // logueamos. Falla acá no impide mostrar los ejercicios; degradación
    // graceful a "ningún completado hoy" (peor caso: primer ejercicio
    // sigue marcado 'actual' incluso si el paciente ya lo hizo).
    if (sesionesHoyRes.error) {
      console.warn(
        "[useEjerciciosDelDia] error cargando sesiones de hoy (degradación: sin completados)",
        sesionesHoyRes.error,
      );
    }

    const completadosHoy = new Set<string>(
      (sesionesHoyRes.data ?? []).map((r) => r.plan_ejercicio_id),
    );

    let primerPendienteAsignado = false;
    const ejercicios: EjercicioHoy[] = (planEjerciciosRes.data ?? []).map(
      (row) => {
        const base = {
          id: row.ejercicio_id,
          planEjercicioId: row.id,
          nombre: row.ejercicios?.nombre ?? row.ejercicio_id,
          reps: row.reps_objetivo,
          series: row.series_objetivo,
        };
        if (completadosHoy.has(row.id)) {
          return { ...base, estado: "completado" as const };
        }
        if (!primerPendienteAsignado) {
          primerPendienteAsignado = true;
          return { ...base, estado: "actual" as const };
        }
        return { ...base, estado: "pendiente" as const };
      },
    );

    setState({ data: ejercicios, error: null, loading: false });
  }, [pacienteId]);

  useEffect(() => {
    const signal = { cancelled: false };
    void fetchAll(signal);
    return () => {
      signal.cancelled = true;
    };
  }, [fetchAll]);

  const refetch = useCallback(async () => {
    await fetchAll();
  }, [fetchAll]);

  return { ...state, refetch };
}
